import React, { useState } from 'react'
import AsyncSelect from 'react-select/async'
import debounce from 'debounce-promise'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons/faInfoCircle'

import { GET_LIST, CREATE, DELETE, useClient } from '@peracto/client'
import { Modal, LoadingIcon } from '@peracto/peracto-ui'
import ArticleAssignmentList from './ArticleAssignmentList'

import { useConfig } from '@peracto/peracto-config'

const ArticleAssignment = ({ articleCategory }) => {
    const { client } = useClient()
    const [listLoading, setListLoading] = useState(false)
    const [itemToAdd, setItemToAdd] = useState()
    const [showAddDialog, setShowAddDialog] = useState(false)

    const onAddArticle = async article => {
        setListLoading(true)
        try {
            await client(CREATE, 'article-category-links', {
                data: {
                    articleCategory: articleCategory.id,
                    article: article.id,
                },
            })
            setListLoading(false)

            toast.success('Article Category Link added successfully!')
        } catch (e) {
            console.error(e)
            const violations = e.error.body.violations
            if (violations && violations.length > 0) {
                const errMsg = violations.find(err => err.propertyPath === 'article')
                toast.error(`Error: ${errMsg.message}`)
            } else {
                toast.error(
                    e?.error?.body?.hasOwnProperty('hydra:description')
                        ? e.error.body['hydra:description']
                        : 'Whoops, there was a problem...'
                )
            }
            setListLoading(false)
        }
    }

    const onDelete = async id => {
        setListLoading(true)
        try {
            await client(DELETE, 'article-category-links', {
                id,
            })
            setListLoading(false)

            toast.success('Article Category Link deleted successfully!')
        } catch (e) {
            console.error(e)
            const violations = e.error.body.violations
            console.log('violations', violations)
            if (violations.length > 0) {
                const errMsg = violations.find(err => err.propertyPath === 'category')
                toast.error(errMsg.message)
            } else {
                toast.error(
                    e?.error?.body?.hasOwnProperty('hydra:description')
                        ? e.error.body['hydra:description']
                        : 'Whoops, there was a problem...'
                )
            }
            setListLoading(false)
        }
    }

    const config = useConfig()
    const { article } = config.get('features', {})

    const fetchArticles = async inputValue => {
        const type = article?.blogToggle ? ['article', 'blog'] : 'article'
        const { data } = await client(GET_LIST, 'contents', {
            id: 'contents',
            filter: {
                type: type,
                name: inputValue,
            },
        })

        return data.map(article => ({
            label: article.name,
            value: article,
        }))
    }

    const debouncedFetchArticles = debounce(fetchArticles, 200)

    if (listLoading) {
        return (
            <div className="!flex !grow !items-center !flex-col !justify-center !py-12 !h-full">
                <LoadingIcon />
            </div>
        )
    }

    return (
        <>
            <div className="row">
                <div className="col-12">
                    <div data-testid="add-articles-dropdown">
                        <AsyncSelect
                            className="!w-full !mb-4"
                            classNamePrefix="list"
                            loadOptions={input => debouncedFetchArticles(input)}
                            isSearchable={true}
                            onChange={option => {
                                setItemToAdd(option)
                                setShowAddDialog(true)
                            }}
                            value={null}
                            defaultOptions={true}
                            placeholder="Search for Articles to add..."
                            noOptionsMessage={({ inputValue }) => {
                                if (inputValue.length > 0) {
                                    return `No results found for '${inputValue}'.`
                                } else {
                                    return 'Enter text to begin searching.'
                                }
                            }}
                        />
                    </div>
                </div>
            </div>

            <ArticleAssignmentList articleCategory={articleCategory} onDelete={onDelete} />

            <Modal
                isVisible={showAddDialog}
                title="Add Article Link"
                close={() => setShowAddDialog(false)}
                buttons={[
                    {
                        type: 'btn-outline-secondary',
                        text: 'Close',
                        action: () => {
                            setShowAddDialog(false)
                        },
                    },
                    {
                        type: 'btn-success',
                        text: 'Add Article',
                        action: () => {
                            setShowAddDialog(false)
                            onAddArticle(itemToAdd.value)
                        },
                    },
                ]}
            >
                <FontAwesomeIcon icon={faInfoCircle} size="4x" className="!block !mb-6" />
                Are you sure you would like to add{' '}
                {itemToAdd?.label ? itemToAdd.label : 'this article'} to this category?
            </Modal>
        </>
    )
}

export default ArticleAssignment
